import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatInTimezone, useTimezone, PAGE } from '@kioskgaming/ui';
import AppLayout from '../../components/layout/AppLayout';
import { getShopGameProviders } from '../../services/api';
import type { ShopGameProvider, ShopGameProvidersResponse } from '../../types';

function syncStatusBadge(status: string) {
  if (status === 'in_sync') return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (status === 'pending') return 'bg-amber-50 text-amber-700 border-amber-200';
  if (status === 'failed') return 'bg-red-50 text-red-700 border-red-200';
  return 'bg-stone-100 text-stone-600 border-stone-200';
}

function ProviderAvatar({ code }: { code: string }) {
  return (
    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-sm font-bold text-blue-700">
      {code.slice(0, 2).toUpperCase()}
    </div>
  );
}

function KpiCard({ label, value, color }: { label: string; value: number | string; color?: string }) {
  return (
    <div className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-500">{label}</p>
      <p className={`mt-1.5 text-3xl font-bold tabular-nums ${color ?? 'text-stone-900'}`}>{value}</p>
    </div>
  );
}

export default function GameWalletsOverviewPage() {
  const navigate = useNavigate();
  const { timezone } = useTimezone();
  const [providers, setProviders] = useState<ShopGameProvider[]>([]);
  const [summary, setSummary] = useState<ShopGameProvidersResponse['summary'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    getShopGameProviders()
      .then((res) => {
        if (res.success && res.data) {
          setProviders(res.data.items);
          setSummary(res.data.summary);
        } else {
          setError(res.message || 'Failed to load game wallets');
        }
      })
      .catch(() => setError('Failed to load game wallets'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return providers.filter((p) => {
      const matchSearch = !q || p.name.toLowerCase().includes(q) || p.code.toLowerCase().includes(q);
      const matchStatus =
        !statusFilter ||
        (statusFilter === 'failed' && p.failed > 0) ||
        (statusFilter === 'pending' && p.pending > 0) ||
        (statusFilter === 'in_sync' && p.inSync > 0) ||
        (statusFilter === 'never_synced' && p.neverSynced > 0);
      return matchSearch && matchStatus;
    });
  }, [providers, search, statusFilter]);

  return (
    <AppLayout>
      <div className="space-y-5">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-stone-900">{PAGE.gameWallets}</h1>
          <p className="mt-1 text-sm text-stone-500">
            Monitor game wallets for Players under this Shop. Read-only — no provider settings or Admin-level controls.
          </p>
        </div>

        {summary && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            <KpiCard label="Total providers" value={summary.totalProviders} />
            <KpiCard label="Player game wallets" value={summary.totalWallets} />
            <KpiCard label="In sync" value={summary.inSync} color="text-emerald-700" />
            <KpiCard label="Pending" value={summary.pending} color="text-amber-700" />
            <KpiCard label="Failed" value={summary.failed} color="text-red-700" />
          </div>
        )}

        <div className="rounded-xl border border-stone-200 bg-white shadow-sm">
          <div className="border-b border-stone-200 px-5 py-4">
            <h2 className="text-base font-semibold text-stone-900">Provider wallet summary</h2>
            <p className="mt-0.5 text-xs text-stone-500">
              Only providers with wallet activity from this Shop's Players are shown.
            </p>
          </div>

          <div className="flex flex-wrap gap-2 px-5 py-3">
            <input
              type="text"
              placeholder="Search provider or code…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-9 rounded-lg border border-stone-200 px-3 text-sm text-stone-900 outline-none placeholder:text-stone-400 focus:border-blue-400 focus:ring-1 focus:ring-blue-200 sm:w-56"
            />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="h-9 rounded-lg border border-stone-200 px-3 text-sm text-stone-700 outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-200"
            >
              <option value="">All sync status</option>
              <option value="in_sync">In sync</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
              <option value="never_synced">Never synced</option>
            </select>
            {(search || statusFilter) && (
              <button
                type="button"
                onClick={() => { setSearch(''); setStatusFilter(''); }}
                className="h-9 rounded-lg border border-stone-200 bg-white px-3 text-sm text-stone-600 hover:bg-stone-50"
              >
                Reset
              </button>
            )}
          </div>

          {loading && (
            <div className="px-5 py-10 text-center text-sm text-stone-400">Loading…</div>
          )}

          {error && (
            <div className="px-5 py-6 text-center text-sm text-red-600">{error}</div>
          )}

          {!loading && !error && (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[760px]">
                <thead>
                  <tr className="border-b border-stone-100 bg-stone-50">
                    {['Provider', 'Total wallets', 'In sync', 'Pending', 'Failed', 'Never synced', 'Last sync', ''].map(
                      (h) => (
                        <th
                          key={h}
                          className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-stone-500"
                        >
                          {h}
                        </th>
                      )
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={8} className="px-5 py-8 text-center text-sm text-stone-400">
                        No providers found
                      </td>
                    </tr>
                  )}
                  {filtered.map((provider) => (
                    <tr
                      key={provider.id}
                      onClick={() => navigate(`/game-wallets/${provider.id}`)}
                      className="cursor-pointer hover:bg-stone-50"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <ProviderAvatar code={provider.code} />
                          <div>
                            <p className="text-sm font-semibold text-stone-900">{provider.name}</p>
                            <p className="text-xs text-stone-400">Code: {provider.code}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm tabular-nums text-stone-700">{provider.totalWallets}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold ${syncStatusBadge('in_sync')}`}>
                          {provider.inSync}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold ${syncStatusBadge('pending')}`}>
                          {provider.pending}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold ${syncStatusBadge('failed')}`}>
                          {provider.failed}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm tabular-nums text-stone-500">{provider.neverSynced}</td>
                      <td className="px-4 py-3 text-sm text-stone-500">
                        {provider.lastSyncAt ? formatInTimezone(provider.lastSyncAt, timezone) : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-700">
                          View detail
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="border-t border-stone-100 px-5 py-3">
            <p className="text-xs text-stone-500">
              <span className="font-semibold">Permission rule:</span> API scopes every result by logged-in Shop ID. Shop cannot access wallets from another Shop by changing URL or API parameters.
            </p>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
